"use client";

import React, { useState, useEffect } from "react";
import { Button } from "./button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card";
import AuthService from "../../services/auth-service";

interface ActivationCode {
  code: string;
  plan: string;
  planLevel: number;
  createdAt: string;
  activated: boolean;
  deviceId?: string;
  activatedAt?: string;
}

export const ActivationCodes: React.FC = () => {
  const [activationCodes, setActivationCodes] = useState<ActivationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch activation codes from the API
  useEffect(() => {
    const fetchActivationCodes = async () => {
      try {
        setLoading(true);
        const user = AuthService.getUser();
        if (!user || !user.id) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }

        // In a real implementation, this would call your API
        // For now, we'll simulate the API call
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://kaya-servers.onrender.com/api'}/auth/plan/user-codes/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${AuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          setActivationCodes(data.codes);
        } else {
          setError(data.message || 'Failed to fetch activation codes');
        }
      } catch (err) {
        setError('An error occurred while fetching activation codes');
        console.error('Error fetching activation codes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivationCodes();
  }, []);

  // Copy code to clipboard
  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  // Generate new activation code (for purchased plans)
  const generateNewCode = async (plan: string) => {
    try {
      // In a real implementation, this would call your API
      alert(`Would generate new code for ${plan} plan`);
    } catch (err) {
      console.error('Error generating activation code:', err);
      alert('Failed to generate activation code');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Desktop Access Codes</CardTitle>
          <CardDescription>Manage your desktop application access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Desktop Access Codes</CardTitle>
          <CardDescription>Manage your desktop application access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Desktop Access Codes</CardTitle>
        <CardDescription>Manage your desktop application access</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activationCodes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">You don't have any activation codes yet.</p>
              <p className="text-sm text-muted-foreground mt-2">Purchase a plan to generate activation codes.</p>
            </div>
          ) : (
            activationCodes.map((code, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <span className="font-mono text-lg font-bold">{code.code}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2"
                        onClick={() => copyToClipboard(code.code)}
                      >
                        Copy
                      </Button>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {code.plan} Plan
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      code.activated 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {code.activated ? 'Activated' : 'Available'}
                    </div>
                    {code.activated && code.deviceId && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Device: {code.deviceId.substring(0, 12)}...
                      </div>
                    )}
                  </div>
                </div>
                
                {code.activated && code.activatedAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Activated on {new Date(code.activatedAt).toLocaleDateString()}
                  </div>
                )}
                
                {!code.activated && (
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" variant="outline">
                      Activate
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        <div className="mt-6 pt-4 border-t">
          <h3 className="font-medium mb-2">Generate New Code</h3>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => generateNewCode('Basic 90')}
            >
              Basic Plan (₹90)
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => generateNewCode('Pro 170')}
            >
              Pro Plan (₹170)
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => generateNewCode('Premium 260')}
            >
              Premium Plan (₹260)
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};